/**
 * CampaignInternalTracker.jsx
 * Drop into: src/pages/Campaigns/.../extras/CampaignInternalTracker.jsx
 */

import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Button, Chip, Paper, Stack, Divider,
  CircularProgress, Tooltip, IconButton, Collapse,
  Table, TableBody, TableCell, TableHead, TableRow,
  LinearProgress, Skeleton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  YouTube as YouTubeIcon,
  MusicVideo as TikTokIcon,
  Instagram as InstagramIcon,
  Sync as SyncIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as ViewsIcon,
  ThumbUp as LikesIcon,
  ChatBubble as CommentsIcon,
  TrackChanges as TrackerIcon,
  Refresh as RefreshIcon,
  BarChart as BarChartIcon,
  Link as LinkIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer,
} from 'recharts';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'sonner';
import client from '../../../../../API';

/* ─── constants ─────────────────────────────────────────────────── */
const RED  = '#dc2626';
const RED2 = '#991b1b';

const PLAT = {
  youtube:   { label: 'YouTube',   color: '#E5001A', bg: '#E5001A12', border: '#E5001A28', Icon: YouTubeIcon   },
  tiktok:    { label: 'TikTok',    color: '#111827', bg: '#11182710', border: '#11182728', Icon: TikTokIcon    },
  instagram: { label: 'Instagram', color: '#C1348A', bg: '#C1348A10', border: '#C1348A28', Icon: InstagramIcon },
};

/* ─── helpers ───────────────────────────────────────────────────── */
function detectPlat(url = '') {
  const l = url.toLowerCase();
  if (l.includes('youtube.com') || l.includes('youtu.be')) return 'youtube';
  if (l.includes('tiktok.com'))    return 'tiktok';
  if (l.includes('instagram.com')) return 'instagram';
  return null;
}
function isTrackable(url) { return !!detectPlat(url); }
function fmtNum(n) {
  if (n == null || n === 0) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}
function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function engRate(views, likes, comments) {
  if (!views) return null;
  return (((likes + comments) / views) * 100).toFixed(1);
}

/* ─── aggregate history → chart data ───────────────────────────── */
function buildChart(historyRaw) {
  if (!Array.isArray(historyRaw) || !historyRaw.length) return [];
  const byDate = {};
  historyRaw.forEach(({ data = [] }) => {
    data.forEach(({ recorded_at, view_count = 0, like_count = 0 }) => {
      const key = (recorded_at || '').slice(0, 10);
      if (!key) return;
      if (!byDate[key]) byDate[key] = { date: key, label: fmtDate(key), views: 0, likes: 0 };
      byDate[key].views += view_count || 0;
      byDate[key].likes += like_count || 0;
    });
  });
  return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
}

/* ─── collect trackable links from campaign JS object ───────────── */
function collectLinks(campaignDetails) {
  const seen = new Set();
  const out  = [];
  (campaignDetails?.creators || []).forEach(creator => {
    const cName = creator.id || creator.name || 'Unknown';
    (creator.videos || []).forEach(v => {
      const url = v.liveLink || v.live_link;
      if (url && isTrackable(url) && !seen.has(url)) {
        seen.add(url);
        out.push({ link: url, notes: cName });
      }
      (v.crossposts || []).forEach(cp => {
        const u = cp.liveLink || cp.live_link;
        if (u && isTrackable(u) && !seen.has(u)) {
          seen.add(u);
          out.push({ link: u, notes: `${cName} (crosspost)` });
        }
      });
    });
  });
  return out;
}

/* ─── recharts custom tooltip ───────────────────────────────────── */
function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: '#0D0D0B', color: '#fff', borderRadius: 2,
      px: 2, py: 1.5, fontSize: '0.78rem', lineHeight: 1.9,
      boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
      border: '1px solid rgba(255,255,255,0.08)',
    }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, opacity: 0.4, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.5 }}>
        {label}
      </Typography>
      {payload.map(p => (
        <Box key={p.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.25 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color, flexShrink: 0 }} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography sx={{ fontSize: '0.68rem', opacity: 0.5 }}>{p.name}</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
              {fmtNum(p.value) || p.value?.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* ─── platform icon box ─────────────────────────────────────────── */
function PlatBox({ url, size = 22 }) {
  const p   = detectPlat(url);
  const cfg = p ? PLAT[p] : null;
  const Icon = cfg?.Icon || LinkIcon;
  return (
    <Box sx={{
      width: size, height: size, borderRadius: 1.25, flexShrink: 0,
      bgcolor: cfg?.color || '#9CA3AF',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon sx={{ fontSize: size * 0.52, color: 'white' }} />
    </Box>
  );
}

/* ─── stat pill ─────────────────────────────────────────────────── */
function StatPill({ icon, value, label, color }) {
  const theme = useTheme();
  const has = value != null && value !== 0;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.25,
      px: 2, py: 1.25,
      borderRadius: 2,
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      flex: '1 1 100px', minWidth: 90,
      bgcolor: has ? alpha(color, 0.04) : 'transparent',
    }}>
      <Box sx={{
        width: 28, height: 28, borderRadius: 1.5, flexShrink: 0,
        bgcolor: alpha(color, has ? 0.12 : 0.06),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 13, color: has ? color : alpha(color, 0.4) } })}
      </Box>
      <Box>
        {has ? (
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color, lineHeight: 1, letterSpacing: '-0.02em' }}>
            {fmtNum(value)}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 500 }}>—</Typography>
        )}
        <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.disabled', mt: 0.25 }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

/* ─── link row ──────────────────────────────────────────────────── */
function LinkRow({ link, creatorName, odd }) {
  const theme    = useTheme();
  const plat     = detectPlat(link.link);
  const cfg      = plat ? PLAT[plat] : null;
  const views    = link.totalViews    || link.view_count    || 0;
  const likes    = link.likes         || link.like_count    || 0;
  const comments = link.comments      || link.comment_count || 0;
  const er       = engRate(views, likes, comments);

  return (
    <TableRow sx={{
      bgcolor: odd ? alpha(theme.palette.divider, 0.02) : 'transparent',
      '&:hover': { bgcolor: alpha(RED, 0.02) },
      transition: 'background 0.1s',
      '&:last-child td': { border: 0 },
    }}>
      {/* Creator + link */}
      <TableCell sx={{ py: 1.25, pl: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <PlatBox url={link.link} size={22} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
              {creatorName || link.notes || '—'}
            </Typography>
            <Typography
              noWrap
              component="a"
              href={link.link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'block', fontSize: '0.62rem', color: 'text.disabled',
                textDecoration: 'none', maxWidth: 200,
                '&:hover': { color: RED },
                transition: 'color 0.15s',
              }}
            >
              {(link.link || '').replace(/^https?:\/\/(www\.)?/, '').slice(0, 48)}
            </Typography>
          </Box>
        </Box>
      </TableCell>

      {/* Platform */}
      <TableCell sx={{ py: 1.25 }}>
        {cfg ? (
          <Chip label={cfg.label} size="small" sx={{
            height: 18, fontSize: '0.58rem', fontWeight: 700,
            bgcolor: cfg.bg, color: cfg.color,
            border: `1px solid ${cfg.border}`,
            '& .MuiChip-label': { px: 0.75 },
          }} />
        ) : <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>—</Typography>}
      </TableCell>

      {/* Views */}
      <TableCell align="right" sx={{ py: 1.25 }}>
        {views > 0
          ? <Typography fontWeight={700} sx={{ color: RED, fontSize: '0.8rem' }}>{fmtNum(views)}</Typography>
          : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
              <CircularProgress size={8} sx={{ color: 'text.disabled', opacity: 0.35 }} />
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>soon</Typography>
            </Box>
          )}
      </TableCell>

      {/* Likes */}
      <TableCell align="right" sx={{ py: 1.25 }}>
        {likes > 0
          ? <Typography fontWeight={600} sx={{ color: '#2563eb', fontSize: '0.8rem' }}>{fmtNum(likes)}</Typography>
          : <Typography variant="caption" color="text.disabled">—</Typography>}
      </TableCell>

      {/* Comments */}
      <TableCell align="right" sx={{ py: 1.25 }}>
        {comments > 0
          ? <Typography fontWeight={600} sx={{ color: '#7c3aed', fontSize: '0.8rem' }}>{fmtNum(comments)}</Typography>
          : <Typography variant="caption" color="text.disabled">—</Typography>}
      </TableCell>

      {/* Engagement */}
      <TableCell align="right" sx={{ py: 1.25 }}>
        {er ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.4 }}>
            <Typography sx={{
              fontSize: '0.75rem', fontWeight: 700,
              color: parseFloat(er) > 5 ? '#16a34a' : parseFloat(er) > 2 ? '#d97706' : 'text.secondary',
            }}>
              {er}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(parseFloat(er) * 10, 100)}
              sx={{
                width: 32, height: 2, borderRadius: 2,
                bgcolor: alpha('#000', 0.06),
                '& .MuiLinearProgress-bar': {
                  bgcolor: parseFloat(er) > 5 ? '#16a34a' : parseFloat(er) > 2 ? '#d97706' : '#9ca3af',
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
      </TableCell>

      {/* Open */}
      <TableCell sx={{ py: 1.25, pr: 2 }}>
        <Tooltip title="Open link" arrow>
          <IconButton
            size="small"
            component="a" href={link.link} target="_blank" rel="noopener noreferrer"
            sx={{
              width: 22, height: 22, color: 'text.disabled',
              '&:hover': { color: RED, bgcolor: alpha(RED, 0.07) },
              transition: 'all 0.15s',
            }}
          >
            <OpenInNewIcon sx={{ fontSize: 11 }} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

/* ─── minimized "no tracker" row ───────────────────────────────── */
function MinimizedRow({ availableLinks, onStart, isCreating }) {
  const theme = useTheme();
  const has   = availableLinks.length > 0;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
      px: 2.5, py: 1.25,
      borderRadius: 2,
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      bgcolor: 'background.paper',
    }}>
      {/* left */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: 1.5, flexShrink: 0,
          bgcolor: has ? alpha(RED, 0.08) : alpha(theme.palette.divider, 0.12),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <TrackerIcon sx={{ fontSize: 13, color: has ? RED : 'text.disabled' }} />
        </Box>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: has ? 'text.primary' : 'text.disabled' }}>
          Campaign Tracker
        </Typography>
        {has ? (
          <Chip
            label={`${availableLinks.length} link${availableLinks.length !== 1 ? 's' : ''} ready`}
            size="small"
            sx={{
              height: 18, fontSize: '0.58rem', fontWeight: 700,
              bgcolor: alpha(RED, 0.07), color: RED,
              border: `1px solid ${alpha(RED, 0.15)}`,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        ) : (
          <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
            No live links yet
          </Typography>
        )}
      </Box>

      {/* right */}
      {has && (
        <Button
          size="small"
          variant="contained"
          startIcon={isCreating ? <CircularProgress size={10} color="inherit" /> : <BoltIcon sx={{ fontSize: 12 }} />}
          onClick={onStart}
          disabled={isCreating}
          sx={{
            flexShrink: 0,
            fontWeight: 700, borderRadius: 1.75, px: 1.75, height: 28,
            fontSize: '0.7rem', letterSpacing: '-0.01em',
            background: `linear-gradient(135deg, ${RED}, ${RED2})`,
            boxShadow: `0 2px 10px ${alpha(RED, 0.28)}`,
            '&:hover': { background: `linear-gradient(135deg, ${RED2}, #7f1d1d)` },
          }}
        >
          {isCreating ? 'Starting…' : 'Start Tracking'}
        </Button>
      )}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN EXPORT
═══════════════════════════════════════════════════════════════════ */
export default function CampaignInternalTracker({ campaignDetails, campaignId }) {
  const theme       = useTheme();
  const queryClient = useQueryClient();
  const [chartOpen, setChartOpen] = useState(true);

  /* links found in this campaign's creator videos (JS-side) */
  const availableLinks = useMemo(() => collectLinks(campaignDetails), [campaignDetails]);

  /* ── tracker fetch ── */
  const { data: trackerRaw, isLoading, refetch } = useQuery(
    ['campaignTracker', campaignId],
    () => client.campaigns.getTracker(campaignId),
    { refetchOnWindowFocus: false, retry: false, onError: () => {} }
  );
  const tracker      = trackerRaw || null;
  const trackerLinks = useMemo(() => Array.isArray(tracker?.links) ? tracker.links : [], [tracker]);

  /* ── history fetch ── */
  const { data: historyRaw } = useQuery(
    ['campaignTrackerHistory', tracker?.id],
    () => client.trackers.getHistory(tracker.id),
    { enabled: !!tracker?.id, refetchOnWindowFocus: false, retry: false }
  );
  const chartData = useMemo(() => buildChart(historyRaw || []), [historyRaw]);
  const hasChart  = chartData.length > 1;

  /* creator name → url lookup */
  const creatorByUrl = useMemo(() => {
    const m = {};
    availableLinks.forEach(l => { m[l.link] = l.notes; });
    return m;
  }, [availableLinks]);

  /* new links not in tracker yet */
  const existingUrls = useMemo(() => new Set(trackerLinks.map(l => l.link)), [trackerLinks]);
  const newLinks     = useMemo(() => availableLinks.filter(l => !existingUrls.has(l.link)), [availableLinks, existingUrls]);

  /* aggregate totals */
  const totals = useMemo(() => ({
    views:    trackerLinks.reduce((s, l) => s + (l.totalViews || l.view_count    || 0), 0),
    likes:    trackerLinks.reduce((s, l) => s + (l.likes      || l.like_count    || 0), 0),
    comments: trackerLinks.reduce((s, l) => s + (l.comments   || l.comment_count || 0), 0),
  }), [trackerLinks]);

  const er = engRate(totals.views, totals.likes, totals.comments);

  const platCounts = useMemo(() => {
    const c = {};
    trackerLinks.forEach(l => { const p = detectPlat(l.link); if (p) c[p] = (c[p] || 0) + 1; });
    return c;
  }, [trackerLinks]);

  /* ── mutations ── */
  const { mutate: createTracker, isLoading: isCreating } = useMutation(
    (links) => client.campaigns.createTracker(campaignId, { links }),
    {
      onSuccess: () => {
        toast.success('Campaign tracker started!');
        refetch();
        queryClient.invalidateQueries(['campaignTracker', campaignId]);
      },
      onError: (e) => toast.error(e?.response?.data?.error || 'Failed to create tracker'),
    }
  );

  const { mutate: syncTracker, isLoading: isSyncing } = useMutation(
    () => client.campaigns.syncTracker(campaignId),
    {
      onSuccess: (data) => {
        const added = data?.added_count ?? data?.added?.length ?? 0;
        if (added > 0) {
          toast.success(`${added} new link${added !== 1 ? 's' : ''} added to tracker`);
        } else {
          toast.info('Tracker is already up to date');
        }
        queryClient.invalidateQueries(['campaignTracker', campaignId]);
        queryClient.invalidateQueries(['campaignTrackerHistory', tracker?.id]);
        refetch();
      },
      onError: (e) => toast.error(e?.response?.data?.error || 'Sync failed'),
    }
  );

  const handleStart = () =>
    createTracker(availableLinks.map(({ link, notes }) => ({ link, notes })));

  const handleRefresh = () => {
    refetch();
    if (tracker?.id) queryClient.invalidateQueries(['campaignTrackerHistory', tracker.id]);
  };

  /* ── loading skeleton ── */
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Skeleton variant="rectangular" height={36} sx={{ flex: 1, borderRadius: 2 }} />
      </Box>
    );
  }

  /* ── no tracker → minimized row ── */
  if (!tracker) {
    return (
      <MinimizedRow
        availableLinks={availableLinks}
        onStart={handleStart}
        isCreating={isCreating}
      />
    );
  }

  /* ── tracker active → full UI ── */
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* ── header bar ── */}
      <Box sx={{
        px: 2.5, py: 1.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.07)}`,
      }}>
        {/* left */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: 1.5, flexShrink: 0,
            background: `linear-gradient(135deg, ${RED}, ${RED2})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 2px 8px ${alpha(RED, 0.28)}`,
          }}>
            <TrackerIcon sx={{ fontSize: 14, color: 'white' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1 }}>
                Campaign Tracker
              </Typography>
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: '9px !important' }} />}
                label="Live"
                size="small"
                sx={{
                  height: 18, fontSize: '0.58rem', fontWeight: 700,
                  bgcolor: '#dcfce7', color: '#15803d',
                  border: '1px solid #bbf7d0',
                  '& .MuiChip-label': { pl: 0.5, pr: 0.75 },
                  '& .MuiChip-icon': { color: '#15803d !important' },
                }}
              />
            </Box>
            <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', mt: 0.3 }}>
              {trackerLinks.length} link{trackerLinks.length !== 1 ? 's' : ''} · updates daily
            </Typography>
          </Box>

          {/* platform chips */}
          {Object.entries(platCounts).map(([p, n]) => {
            const cfg  = PLAT[p];
            const Icon = cfg.Icon;
            return (
              <Chip key={p}
                icon={<Icon sx={{ fontSize: '10px !important', color: `${cfg.color} !important` }} />}
                label={`${n} ${cfg.label}`}
                size="small"
                sx={{
                  height: 20, fontSize: '0.6rem', fontWeight: 700,
                  bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                  '& .MuiChip-label': { pl: 0.5, pr: 0.75 },
                  display: { xs: 'none', sm: 'flex' },
                }}
              />
            );
          })}
        </Box>

        {/* right: actions */}
        <Stack direction="row" spacing={0.75} alignItems="center">
          {newLinks.length > 0 && (
            <Button
              size="small"
              variant="contained"
              startIcon={isSyncing ? <CircularProgress size={10} color="inherit" /> : <SyncIcon sx={{ fontSize: 12 }} />}
              onClick={() => syncTracker()}
              disabled={isSyncing}
              sx={{
                borderRadius: 1.75, fontSize: '0.65rem', fontWeight: 700, height: 26, px: 1.25,
                background: `linear-gradient(135deg, ${RED}, ${RED2})`,
                boxShadow: `0 2px 8px ${alpha(RED, 0.25)}`,
                '&:hover': { background: `linear-gradient(135deg, ${RED2}, #7f1d1d)` },
              }}
            >
              {isSyncing ? 'Syncing…' : `Sync ${newLinks.length} new`}
            </Button>
          )}
          <Tooltip title="Refresh" arrow>
            <IconButton size="small" onClick={handleRefresh}
              sx={{ width: 26, height: 26, color: 'text.disabled', '&:hover': { color: 'text.primary', bgcolor: alpha(theme.palette.divider, 0.1) } }}>
              <RefreshIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
          {tracker.id && (
            <Tooltip title="Open public tracker" arrow>
              <IconButton size="small" component="a" href={`/tracker/${tracker.id}`} target="_blank"
                sx={{ width: 26, height: 26, color: 'text.disabled', '&:hover': { color: 'text.primary', bgcolor: alpha(theme.palette.divider, 0.1) } }}>
                <OpenInNewIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* ── stat strip ── */}
      <Box sx={{ px: 2.5, py: 2, display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
        <StatPill icon={<ViewsIcon />}    value={totals.views}          label="Views"     color={RED}       />
        <StatPill icon={<LikesIcon />}    value={totals.likes}          label="Likes"     color="#2563eb"   />
        <StatPill icon={<CommentsIcon />} value={totals.comments}       label="Comments"  color="#7c3aed"   />
        <StatPill icon={<LinkIcon />}     value={trackerLinks.length}   label="Links"     color="#374151"   />
        {er && (
          <StatPill icon={<TrendingUpIcon />} value={parseFloat(er)} label="Eng %" color="#059669" />
        )}
      </Box>

      {/* pending nudge */}
      {!totals.views && !totals.likes && trackerLinks.length > 0 && (
        <Box sx={{ px: 2.5, pb: 2 }}>
          <Box sx={{
            px: 1.75, py: 1, borderRadius: 1.75,
            bgcolor: alpha('#2563eb', 0.04),
            border: `1px solid ${alpha('#2563eb', 0.12)}`,
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <CircularProgress size={10} sx={{ color: '#2563eb', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.68rem', color: '#1d4ed8', fontWeight: 600, lineHeight: 1.4 }}>
              Metrics populate after the first scraper run — usually within 24 hours.
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── chart ── */}
      {hasChart && (
        <>
          <Divider sx={{ opacity: 0.5 }} />
          <Box
            onClick={() => setChartOpen(p => !p)}
            sx={{
              px: 2.5, py: 1.25,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              cursor: 'pointer',
              '&:hover': { bgcolor: alpha(theme.palette.divider, 0.03) },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BarChartIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>Views Over Time</Typography>
              <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>
                {chartData.length} snapshots
              </Typography>
            </Box>
            {chartOpen
              ? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
          </Box>

          <Collapse in={chartOpen} timeout="auto">
            <Box sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={chartData} margin={{ top: 6, right: 4, left: -28, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={RED}     stopOpacity={0.15} />
                      <stop offset="95%" stopColor={RED}     stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gl" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#2563eb" stopOpacity={0.12} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.04)} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#9CA3AF' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 9, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={v => fmtNum(v) || v} />
                  <ReTooltip content={<ChartTip />} cursor={{ stroke: alpha(RED, 0.12), strokeWidth: 1 }} />
                  <Area type="monotone" dataKey="views" name="Views" stroke={RED} strokeWidth={2} fill="url(#gv)" dot={false} activeDot={{ r: 4, fill: RED, stroke: 'white', strokeWidth: 2 }} />
                  <Area type="monotone" dataKey="likes" name="Likes" stroke="#2563eb" strokeWidth={1.5} fill="url(#gl)" dot={false} activeDot={{ r: 3.5, fill: '#2563eb', stroke: 'white', strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Collapse>
        </>
      )}

      {/* ── link table ── */}
      <Divider sx={{ opacity: 0.5 }} />

      <Box sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinkIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>Tracked Links</Typography>
        <Chip
          label={trackerLinks.length}
          size="small"
          sx={{
            height: 16, fontSize: '0.58rem', fontWeight: 700,
            bgcolor: alpha(RED, 0.07), color: RED,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      </Box>

      {trackerLinks.length === 0 ? (
        <Box sx={{ px: 2.5, py: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
            No links yet. Add live links to creator videos, then click "Sync new".
          </Typography>
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                {['Creator / Link', 'Platform', 'Views', 'Likes', 'Comments', 'Engagement', ''].map((h, i) => (
                  <TableCell
                    key={i}
                    align={i >= 2 && i <= 5 ? 'right' : 'left'}
                    sx={{
                      fontSize: '0.54rem', fontWeight: 800, color: 'text.disabled',
                      textTransform: 'uppercase', letterSpacing: '0.1em',
                      py: 0.75, border: 0,
                      pl: i === 0 ? 3 : undefined,
                      pr: i === 6 ? 2 : undefined,
                      bgcolor: alpha(theme.palette.divider, 0.025),
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {trackerLinks.map((link, i) => (
                <LinkRow
                  key={link.link || i}
                  link={link}
                  creatorName={creatorByUrl[link.link] || link.notes}
                  odd={i % 2 === 1}
                />
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* ── new links alert ── */}
      {newLinks.length > 0 && (
        <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.07)}` }}>
          <Box sx={{
            px: 2, py: 1.25, borderRadius: 2,
            bgcolor: alpha('#d97706', 0.04),
            border: `1px solid ${alpha('#d97706', 0.15)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon sx={{ fontSize: 13, color: '#d97706', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.7rem', color: '#92400e', fontWeight: 600 }}>
                <strong>{newLinks.length} new link{newLinks.length !== 1 ? 's' : ''}</strong> not yet tracked.
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={() => syncTracker()}
              disabled={isSyncing}
              startIcon={isSyncing ? <CircularProgress size={10} color="inherit" /> : <SyncIcon sx={{ fontSize: 11 }} />}
              sx={{
                flexShrink: 0, fontWeight: 700, fontSize: '0.65rem',
                borderRadius: 1.5, height: 24, px: 1.25,
                bgcolor: '#d97706', color: 'white',
                '&:hover': { bgcolor: '#b45309' },
              }}
            >
              {isSyncing ? 'Adding…' : 'Add now'}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
}